import { Box, IconButton, Stack, Typography } from "@mui/material";
import useWavesurfer from "./useWaveSurfer";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Images } from "../../assets/common";
import { HMS } from "../../constants/function";

const MOBILE_WAVE_HEIGHT = 30;
const DESKTOP_WAVE_HEIGHT = 22;

export default function WaveSurferPlayer({
  url,
  handleNext,
  handlePrev,
  handlePlay,
  handleCurrentTime,
  handleEnd,
  handleSeek,
  handleDuration,
  sampleData,
  volume,
  showAdv,
  currentPlayTime,
  duration,
  autoplay = true,
  onMediaError,
  startAt = 0,
  trailingControl = null,
  // "mobile" renders the original compact transport; "desktop" the redesign.
  variant = "desktop",
}) {
  const isMobile = variant === "mobile";
  const containerRef = useRef();
  const [isPlaying, setIsPlaying] = useState(false);
  const wavesurfer = useWavesurfer(containerRef, url, sampleData, { autoplay, onMediaError, startAt });
  // "ready" fires again when real peaks replace the placeholder waveform;
  // only the first one per song should reset the clock.
  const announcedReadyRef = useRef(false);

  const onPlayClick = useCallback(() => {
    // Resume any suspended AudioContext (Safari requires this from a user gesture)
    if (window._wavesurferAudioCtx && window._wavesurferAudioCtx.state === 'suspended') {
      window._wavesurferAudioCtx.resume();
    }
    wavesurfer.isPlaying() ? wavesurfer.pause() : wavesurfer.play();
  }, [wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;
    if (typeof wavesurfer.setVolume === 'function') {
      wavesurfer.setVolume(volume / 100);
    } else {
      const media = wavesurfer.getMediaElement();
      if (media) media.volume = volume / 100;
    }
  }, [volume]);
  
  // An ad pauses the song and, when it ends, resumes only if the ad was what
  // paused it. This used to call the play/pause toggle, which restarted songs
  // the user had paused and paused songs that were already stopped.
  const pausedByAdRef = useRef(false);
  useEffect(() => {
    if (!wavesurfer) return;
    if (showAdv) {
      pausedByAdRef.current = wavesurfer.isPlaying();
      if (pausedByAdRef.current) wavesurfer.pause();
    } else if (pausedByAdRef.current) {
      pausedByAdRef.current = false;
      wavesurfer.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdv]);

  // Listen for global stop event
  useEffect(() => {
    const handleStopAllPlayers = (event) => {
      console.log('[WaveSurferPlayer] Received stop-all-players event');
      if (wavesurfer && wavesurfer.isPlaying()) {
        wavesurfer.pause();
      }
    };

    window.addEventListener('stop-all-players', handleStopAllPlayers);

    return () => {
      window.removeEventListener('stop-all-players', handleStopAllPlayers);
    };
  }, [wavesurfer]);

  // Pause when network drops to prevent auto-resume on reconnect
  useEffect(() => {
    if (!wavesurfer) return;

    const handleOffline = () => {
      if (wavesurfer.isPlaying()) {
        console.log('[WaveSurferPlayer] Network lost, pausing playback');
        wavesurfer.pause();
      }
    };

    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;
    announcedReadyRef.current = false;
    // Playback starts before "ready", so apply the volume now rather than
    // waiting for metadata.
    if (typeof wavesurfer.setVolume === 'function') {
      wavesurfer.setVolume(volume / 100);
    } else {
      const media = wavesurfer.getMediaElement();
      if (media) media.volume = volume / 100;
    }

    const subscriptions = [
      wavesurfer.on("ready", () => {
        if (announcedReadyRef.current) return;
        announcedReadyRef.current = true;
        const duration = wavesurfer.getDuration();
        if (handleDuration) handleDuration(duration);
        handleCurrentTime(0);
        // Resume AudioContext if suspended (Safari autoplay policy)
        if (window._wavesurferAudioCtx && window._wavesurferAudioCtx.state === 'suspended') {
          window._wavesurferAudioCtx.resume();
        }
      }),
      wavesurfer.on("play", () => {
        setIsPlaying(true);
        handlePlay(true);
      }),
      wavesurfer.on("pause", () => {
        setIsPlaying(false);
        handlePlay(false);
      }),
      wavesurfer.on("timeupdate", (currentTime) => {
        handleCurrentTime(currentTime);
      }),
      wavesurfer.on("interaction", (currentTime) => {
        handleSeek(currentTime);
      }),
      wavesurfer.on("finish", () => {
        // wavesurfer emits "finish" without a "pause" event, so clear the
        // playing state here. Otherwise the button keeps showing Pause after
        // the last song ends and the next click restarts it instead of
        // stopping it.
        setIsPlaying(false);
        handlePlay(false);
        handleEnd(wavesurfer.getDuration());
      }),
    ];

    // Playback is started inside useWavesurfer before this effect runs, so
    // the "play" event has usually already fired. Sync the initial state.
    const alreadyPlaying = wavesurfer.isPlaying();
    setIsPlaying(alreadyPlaying);
    if (alreadyPlaying) handlePlay(true);

    return () => {
      subscriptions.forEach((unsub) => unsub());
    };
  }, [wavesurfer, url]);

  // The desktop bar uses a slimmer waveform than mobile. Both variants share
  // one wave container, so switching only resizes the existing waveform.
  useEffect(() => {
    if (!wavesurfer) return;
    wavesurfer.setOptions({ height: isMobile ? MOBILE_WAVE_HEIGHT : DESKTOP_WAVE_HEIGHT });
  }, [wavesurfer, isMobile]);

  // Children keep the same positions in both variants so React keeps the wave container, and the waveform drawn into it,
  // when the layout switches at the mobile breakpoint.
  return (
    <div className={isMobile ? "surfer_bar" : "sk-transport"}>
      {isMobile ? (
        <MobileTransportButtons
          isPlaying={isPlaying}
          onPrev={handlePrev}
          onPlay={onPlayClick}
          onNext={handleNext}
        />
      ) : (
        <div className="sk-transport__controls">
          <div className="sk-transport__buttons">
            <IconButton
              aria-label="Previous track"
              title="Previous"
              className="sk-transport__button"
              onClick={handlePrev}
            >
              <SkipPreviousRoundedIcon />
            </IconButton>
            <IconButton
              aria-label={isPlaying ? "Pause" : "Play"}
              title={isPlaying ? "Pause" : "Play"}
              className="sk-transport__play"
              onClick={onPlayClick}
            >
              {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
            </IconButton>
            <IconButton
              aria-label="Next track"
              title="Next"
              className="sk-transport__button"
              onClick={handleNext}
            >
              <SkipNextRoundedIcon />
            </IconButton>
          </div>
          <div className="sk-transport__trailing">{trailingControl}</div>
        </div>
      )}
      <div
        ref={containerRef}
        className={isMobile ? undefined : "sk-transport__wave"}
        style={isMobile ? { flex: 1, minWidth: 0, cursor: "pointer", overflow: "hidden" } : undefined}
      />
      {isMobile ? (
        <Typography
          variant="caption"
          sx={{
            color: "#aaa",
            fontSize: "11px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {HMS(Math.floor(currentPlayTime || 0)) || "0:00"} / {HMS(Math.floor(duration || 0)) || "0:00"}
        </Typography>
      ) : (
        <span className="sk-transport__time">
          <span className="sk-transport__time--elapsed">{formatPlaybackTime(currentPlayTime)}</span>
          {" / "}
          <span className="sk-transport__time--total">{formatPlaybackTime(duration)}</span>
        </span>
      )}
    </div>
  );
}

// Mobile prev / play / next, unchanged from the original bar.
function MobileTransportButtons({ isPlaying, onPrev, onPlay, onNext }) {
  const [isHoverLeft, setIsHoverLeft] = useState(false);
  const [isHoverRight, setIsHoverRight] = useState(false);
  return (
    <Stack sx={{ justifyContent: "center", alignItems: "center" }} direction="row" spacing={2}>
      <Box
        aria-label="prev song"
        onClick={onPrev}
        className="_cp"
        onMouseEnter={() => setIsHoverLeft(true)}
        onMouseLeave={() => setIsHoverLeft(false)}
      >
        <img src={isHoverLeft ? Images.prev_icon_hover : Images.prev_icon} alt="" />
      </Box>
      <IconButton aria-label="play song" sx={{ backgroundColor: "#2E2E2D" }} onClick={onPlay}>
        {!isPlaying ? (
          <PlayArrowRoundedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
        ) : (
          <PauseOutlinedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
        )}
      </IconButton>
      <Box
        aria-label="next song"
        onClick={onNext}
        className="_cp"
        onMouseEnter={() => setIsHoverRight(true)}
        onMouseLeave={() => setIsHoverRight(false)}
      >
        <img src={isHoverRight ? Images.next_icon_hover : Images.next_icon} alt="" />
      </Box>
    </Stack>
  );
}

/** Seconds as m:ss (or h:mm:ss), the way streaming players show time. */
export function formatPlaybackTime(value) {
  const total = Math.max(Math.floor(Number(value) || 0), 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`
    : `${minutes}:${seconds}`;
}
